import React, { useEffect, useRef, useState } from "react";
import { RangeInput, Checkbox, Button } from "../../..";
import { FiFilter } from "react-icons/fi";
import ViewYourPolicy from "../../../ViewYourPolicy";
import { formatedPrice, TIME_SLOTS } from "../../../../Utils/utils";

function RoundtripFlightFilters({ currency, flights = [], filters = {}, updateFilters = () => {}, fromCode, toCode }) {
	const [Airlines, setAirlines] = useState([]);
	const [MaxRange, setMaxRange] = useState(0);
	const [MinRange, setMinRange] = useState(0);
	const prevAvailableAirlinesRef = useRef([]);

	const stopScrollPropagation = (event) => {
		// Keep scroll interaction scoped to the filter pane on mobile drawer.
		event.stopPropagation();
	};
	useEffect(() => {
		const airlines = [];
		let maxPrice = 0;
		let minPrice;
		flights.forEach((flight) => {
			flight.fares.forEach((fare) => {
				if (fare?.price > maxPrice) {
					maxPrice = fare?.price;
				}
				if (minPrice === undefined || fare?.price < minPrice) {
					minPrice = fare?.price;
				}
			});
			flight.segments?.forEach((segment) => {
				if (!airlines.includes(segment.airlineName)) {
					airlines.push(segment.airlineName);
				}
			});
			flight.onwardSegments?.forEach((segment) => {
				if (!airlines.includes(segment.airlineName)) {
					airlines.push(segment.airlineName);
				}
			});
		});
		setAirlines(airlines);
		setMaxRange(maxPrice);
		setMinRange(minPrice);
		updateFilters((prev) => {
			const stillSelected = (prev.airlines ?? []).filter((name) => airlines.includes(name));
			const newlyAvailable = airlines.filter((name) => !prevAvailableAirlinesRef.current.includes(name));
			const nextAirlines = airlines.filter((name) => stillSelected.includes(name) || newlyAvailable.includes(name));
			prevAvailableAirlinesRef.current = [...airlines];
			return {
				...prev,
				...filters,
				maxPrice,
				minPrice,
				airlines: nextAirlines,
			};
		});
	}, [flights]);

	const applyFilter = (type, checked, value) => {
		const current = filters[type] ?? [];
		if (checked) {
			updateFilters({
				...filters,
				[type]: [...current, value],
			});
		} else {
			updateFilters({
				...filters,
				[type]: current.filter((a) => a !== value),
			});
		}
	};

	const renderTimeFilter = (title, type) => (
		<div className="mt-6">
			<div className="mb-2 text-sm font-semibold">{title}</div>
			<div>
				{TIME_SLOTS.map((slot) => (
					<div key={slot.value} className="mt-2 flex-center-between whitespace-nowrap">
						<div className="align-center">
							<Checkbox checked={(filters[type] ?? []).includes(slot.value)} onChange={(checked) => applyFilter(type, checked, slot.value)} />
							<span className="mr-1">{slot.icon}</span>
							<span className="text-xs text-secondary whitespace-nowrap">{slot.label}</span>
						</div>
						<div className="text-xs text-secondary whitespace-nowrap">{slot.time}</div>
					</div>
				))}
			</div>
		</div>
	);

	return (
		<div className="flex flex-col justify-between h-full min-h-0">
			<div className="flex-1 min-h-0 pr-1 overflow-y-auto overscroll-contain" onWheelCapture={stopScrollPropagation} onTouchMoveCapture={stopScrollPropagation}>
				<div className="mb-4">
					<ViewYourPolicy />
				</div>
				<div className="mt-2 align-center">
					<FiFilter className="mr-2" />
					<h5>Flight filters</h5>
				</div>
				<div className="mt-6">
					<div className="mb-2 text-sm font-semibold">Stops</div>
					<div>
						<Checkbox checked={filters.stops.includes(0)} onChange={(checked) => applyFilter("stops", checked, 0)} label="Non-Stop" className="mt-2" />
						<Checkbox checked={filters.stops.includes(1)} onChange={(checked) => applyFilter("stops", checked, 1)} label="1 Stop" className="mt-2" />
						<Checkbox checked={filters.stops.includes(2)} onChange={(checked) => applyFilter("stops", checked, 2)} label="2 stop" className="mt-2" />
					</div>
				</div>

				<div className="mt-6">
					<div className="mb-2 text-sm font-semibold">Quick filters</div>
					<div>
						<Checkbox checked={filters.inPolicy} onChange={(checked) => updateFilters((prev) => ({ ...prev, inPolicy: checked }))} label="Fares that are in my policy" className="mt-2" />
						<Checkbox checked={filters.fullyRefundable} onChange={(checked) => updateFilters((prev) => ({ ...prev, fullyRefundable: checked }))} label="Fully refundable fares" className="mt-2" />
					</div>
				</div>

				<div className="mt-6">
					<div className="text-sm font-semibold flex-center-between">
						<div className="mb-2">Price</div>
						<div className="font-bold text-dark">
							{currency} {formatedPrice(filters.maxPrice?.toFixed())}
						</div>
					</div>
					<RangeInput min={MinRange} max={MaxRange} value={filters.maxPrice} onChange={(value) => updateFilters({ ...filters, maxPrice: Number(value) })} unit={currency} />
				</div>

				<div className="mt-6">
					<div className="mb-2 text-sm font-semibold">Airlines</div>
					<div>
						<Checkbox
							checked={Airlines.every((airline) => filters.airlines.includes(airline))}
							onChange={(checked) => {
								const allAirlines = checked ? [...Airlines] : [];
								updateFilters({ ...filters, airlines: allAirlines });
							}}
							label={Airlines.every((airline) => filters.airlines.includes(airline)) ? "Clear All" : "Select All"}
							className="mb-2"
							highlight
						/>
						{Airlines.map((airline, i) => (
							<div key={i} className="relative group flex items-center justify-between mt-2">
								<Checkbox checked={filters.airlines.includes(airline)} onChange={(checked) => applyFilter("airlines", checked, airline)} label={airline} />

								<button
									onClick={() => updateFilters({ ...filters, airlines: [airline] })}
									className="text-xs text-mainBlue shrink-0 ml-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity"
								>
									Only
								</button>
							</div>
						))}
					</div>
				</div>

				{renderTimeFilter(`Departure from ${fromCode?.toUpperCase() || "origin"}`, "onwardDepartureTime")}
				{renderTimeFilter(`Arrival in ${toCode?.toUpperCase() || "destination"}`, "onwardArrivalTime")}
				{renderTimeFilter(`Departure from ${toCode?.toUpperCase() || "destination"}`, "returnDepartureTime")}
				{renderTimeFilter(`Arrival in ${fromCode?.toUpperCase() || "origin"}`, "returnArrivalTime")}
			</div>

			<div className="pt-3 pb-6 sm:pb-4">
				<Button
					onClick={() =>
						updateFilters({
							stops: [],
							airlines: [...Airlines],
							onwardDepartureTime: [],
							onwardArrivalTime: [],
							returnDepartureTime: [],
							returnArrivalTime: [],
							inPolicy: false,
							fullyRefundable: false,
							maxPrice: MaxRange,
						})
					}
					variant="dark"
					className="w-full btn-sm"
				>
					Reset filters
				</Button>
			</div>
		</div>
	);
}

export default RoundtripFlightFilters;
